import { Form, Formik } from 'formik';
import { useRouter, useSearchParams } from 'next/navigation';
import qs from 'query-string';
import { FC, ReactNode } from 'react';
import { shallow } from 'zustand/shallow';

import { GetStartedMicrocopyProps } from 'src/checkout/sections/GetStarted/GetStarted';
import {
  GetStartedFormValidation,
  GetStartedFormValues,
  TRADE_IN_TYPE,
  USE_FINANCE_TYPE,
} from 'src/checkout/templates/Checkout/Forms/CheckoutForm.type';
import { IconClock } from 'src/general/Icons/IconClock';
import { Button } from 'src/general/components/Button/Button';
import { InputRadioFormik } from 'src/general/components/forms/Input/InputRadioFormik';
import { flattenQueryParam } from 'src/general/helpers/flattenQueryParam';
import { authStore } from 'src/stores/authStore';
import { BaseCarData } from 'src/types/Car.types';
import { baseCarDataToDataLayerItem, pushToDataLayer } from 'src/utils/pushToDataLayer';

import styles from './GetStartedForm.module.scss';

interface GetStartedFormProps {
  microcopy: GetStartedMicrocopyProps;
  carData: BaseCarData;
  moreDetailsComponent?: ReactNode;
}

const initialValues: GetStartedFormValues = {
  useFinance: '' as any,
  tradeIn: '' as any,
};

export const GetStartedForm: FC<GetStartedFormProps> = ({ microcopy, carData, moreDetailsComponent }) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const hasTradeIn = flattenQueryParam(searchParams?.get('hasTradeIn') ?? undefined);
  const useFinance = flattenQueryParam(searchParams?.get('useFinance') ?? undefined);
  const { isUserLoggedIn } = authStore(({ isUserLoggedIn }) => ({ isUserLoggedIn }), shallow);

  const {
    option1Title,
    option1Description,
    option2Title,
    option2Description,
    holdCarText,
    holdCarTime,
    submitButtonLabel,
  } = microcopy;

  const values = () => {
    let finance: USE_FINANCE_TYPE = initialValues.useFinance;
    let tradeIn: TRADE_IN_TYPE = initialValues.tradeIn;

    if (useFinance === 'yes') finance = USE_FINANCE_TYPE.FINANCE;
    else if (useFinance === 'no') finance = USE_FINANCE_TYPE.NO_FINANCE;

    if (hasTradeIn === 'yes') tradeIn = TRADE_IN_TYPE.TRADE_IN;
    else if (hasTradeIn === 'no') tradeIn = TRADE_IN_TYPE.NO_TRADE_IN;

    return {
      useFinance: finance,
      tradeIn: tradeIn,
    };
  };

  const getStartedFinance = [
    { value: USE_FINANCE_TYPE.FINANCE, label: 'Yes' },
    { value: USE_FINANCE_TYPE.NO_FINANCE, label: 'No' },
  ];

  const getStartedTradeIn = [
    { value: TRADE_IN_TYPE.TRADE_IN, label: 'Yes' },
    { value: TRADE_IN_TYPE.NO_TRADE_IN, label: 'No' },
  ];

  const onSubmit = (values: GetStartedFormValues) => {
    const { tradeIn, useFinance } = values;
    pushToDataLayer({
      event: 'checkout_attempt',
      finance: useFinance,
      tradeIn,
      ecommerce: {
        items: [baseCarDataToDataLayerItem(carData)],
      },
    });

    const checkoutUrl = `/checkout?${qs.stringify({
      sku: carData.sku,
      hasTradeIn: tradeIn,
      useFinance,
    })}`;

    if (isUserLoggedIn) {
      router.push(checkoutUrl);
    } else {
      const originUrl = `/product-detail/${carData.sku}/get-started?${qs.stringify({
        hasTradeIn: tradeIn,
        useFinance,
      })}`;

      router.push(
        `/signin?${qs.stringify({
          origin: originUrl,
          redirect: checkoutUrl,
        })}`,
      );
    }
  };

  return (
    <div className={styles.formWrapper}>
      <Formik
        initialValues={{ ...initialValues, ...values() }}
        onSubmit={onSubmit}
        validationSchema={GetStartedFormValidation}
      >
        {({ errors, touched, values, ...props }) => (
          <Form className={styles.form}>
            <div>
              <p className={styles.radioItemTitle}>{option1Title}</p>
              <p className={styles.radioItemDescription}>{option1Description}</p>
              <InputRadioFormik
                id="useFinance"
                name="useFinance"
                options={getStartedFinance.map((item) => ({
                  value: item.value,
                  label: item.label,
                }))}
                variant="card"
                className={styles.radios}
              />
            </div>

            <div>
              <p className={styles.radioItemTitle}>{option2Title}</p>
              <p className={styles.radioItemDescription}>{option2Description}</p>
              <InputRadioFormik
                id="tradeIn"
                name="tradeIn"
                options={getStartedTradeIn.map((item) => ({
                  value: item.value,
                  label: item.label,
                }))}
                variant="card"
                className={styles.radios}
              />
            </div>

            {moreDetailsComponent ?? null}

            <div className={styles.buttonContainer}>
              <div className={styles.holdCarTextContainer}>
                <p className={styles.holdCarText}>{holdCarText}</p>
                <IconClock className={styles.iconClock} />
                <p className={styles.holdCarText}>{holdCarTime}</p>
              </div>
              <Button type="submit" isLoading={props.isSubmitting} fullWidth prefetch={false}>
                {submitButtonLabel}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};
